import React from "react";
import Widget from "../Widget";
import * as d3 from "d3";
import Draggable from "react-draggable";
import { observer } from "mobx-react";
import { action, computed, makeObservable } from "mobx";
import { withRouter } from "react-router-dom";
import ResizeSensor from "css-element-queries/src/ResizeSensor";
import { formatDateTime } from "../../shared/dateTimeUtils";
import * as _ from "lodash";
import Toggle from "../../shared/Toggle/Toggle";
import ECGStats from "./ECGStats";
import dropdown from "./dropdown.svg";
import { DateTime } from "luxon";

// This component contains both display for 12-lead and single-lead.
class ECG12Lead extends Widget {
  _hScroll = 0;
  _vScroll = 0;
  dragTarget = null;
  stripChartsCache = {};

  constructor(props) {
    super(props);

    makeObservable(this, {
      setLead: action,
      wheel: action,
      zoom: action,
      leadMouseDown: action,
      dragLead: action,
      endDrag: action,
      handleDrag: action,
      updateQuarterLeads: action,
      setSizes: action,
      rescaleScrubber: action,
      scrubberMouseDown: action,
      handleDragCaliper: action,
      caliperDistances: computed,
    });

    this.store = this.props.ecgStore.chartsStore || {};
    this.store.ecg = this.props.ecg;
    this.prevNextDisabled = false;

    this.zoom = this.zoom.bind(this);
    this.handleDrag = this.frameDebounce(this.handleDrag.bind(this)).bind(this);
    this.selectLead = this.props.selectLead;
    this.setLead = this.setLead.bind(this);
    this.setSizes = this.frameDebounce(this.setSizes.bind(this)).bind(this);
    this.wheel = this.frameDebounce(this.wheel.bind(this)).bind(this);
    this.leadMouseDown = this.leadMouseDown.bind(this);
    this.dragLead = this.frameDebounce(this.dragLead.bind(this)).bind(this);
    this.endDrag = this.endDrag.bind(this);
    this.scrubberMouseDown = this.scrubberMouseDown.bind(this);
    this.toggleMarchOut = this.toggleMarchOut.bind(this);
    this.startDragBothCalipers = this.startDragBothCalipers.bind(this);
    this.handleOverDrag = this.frameDebounce(
      this.handleOverDrag.bind(this),
    ).bind(this);

    // register an unregister an event listener for route changes to drive lead selection.
    this.unlisten = this.props.history.listen((location) => {
      if (location.pathname.match(/\/ecg/g)) {
        setImmediate(() => {
          this.setLead();
        });
      }
    });
  }

  render() {
    let leads = this.store.selectedLead
      ? [this.store.selectedLead]
      : this.props.ecg.leads;
    let date = DateTime.fromISO(this.store.ecg.ecgDetails.acquisitionDateTime);

    const lastSelectedLeadIndex = 0;

    const vertical = this.props.caliperMode === "vertical";

    const markerPositions = this.store.markers.map((marker) => {
      return {
        x: this.store.timeToPosition(marker.time),
        y: this.store.voltageToPosition(marker.voltage),
        //...marker.position
      };
    });

    return (
      <div
        className={this.cn({
          "ecg-12-lead": 1,
          "single-lead": this.store.selectedLead,
        })}
      >
        <div className="ecg-header" data-testid="ecg-header">
          <div className="header-info">
            <div className="date">{formatDateTime(date, "•")}</div>
            <div className="diagnosis">
              <img
                src={dropdown}
                className="icon"
                alt="details"
                onClick={() =>
                  (this.store.displayDetails = !this.store.displayDetails)
                }
              />
              {this.store.ecg.ecgDetails.diagnosis}
            </div>
          </div>

          <div className="header-stats">
            <div className="stat-item">
              V.HR <span className="stat-unit">(bpm)</span>:{" "}
              {this.store.ecg.ecgDetails.vhr}
            </div>
            <div className="stat-item">
              A.HR <span className="stat-unit">(bpm)</span>:{" "}
              {this.store.ecg.ecgDetails.ahr}
            </div>
          </div>
        </div>
        {this.store.displayDetails ? (
          <div className="ecg-details">
            <table className="patient-info">
              <tbody>
                <tr>
                  <td className="label">Sex</td>
                  <td className="value">{this.store.ecg.ecgDetails.sex}</td>
                </tr>
                <tr>
                  <td className="label">DOB</td>
                  <td className="value">
                    {this.store.ecg.ecgDetails.dob ||
                      _.get(this.props, "store.patient.dob")}
                  </td>
                </tr>
                <tr>
                  <td className="label">Age</td>
                  <td className="value">{this.store.ecg.ecgDetails.age}</td>
                </tr>
                <tr>
                  <td className="label">Race</td>
                  <td className="value">
                    {this.store.ecg.ecgDetails.race ||
                      _.get(this.props, "store.patient.race")}
                  </td>
                </tr>
              </tbody>
            </table>
            <ECGStats ecg={this.store.ecg} hr={false} />
            <div className="analysis">{this.store.ecg.ecgDetails.analysis}</div>
          </div>
        ) : null}
        <div className="ecg-12-lead-body" ref={(r) => (this.refChartRoot = r)}>
          {this.props.caliperMode === "horizontal" ? (
            <div className="ecg-grid-header">
              <div className="march-out-toggle">
                <Toggle
                  onChange={this.toggleMarchOut}
                  label="Enable March Out"
                  checked={this.store.marchOutMode}
                />
              </div>
            </div>
          ) : null}
          <div className="ecg-grid" ref={(r) => (this.refGrid = r)}>
            {leads.slice(0, 12).map((lead, i) => (
              <div
                className={this.cn({
                  "canvas-holder": 1,
                  inactive:
                    this.store.selectedLead && lead !== this.store.selectedLead,
                })}
                key={i}
                data-key={lead.title}
              >
                <div className="title">{lead.title}</div>
                {i === 0 ? <div className="scale-key">0.1mV/.04sec</div> : null}
                <canvas
                  ref={(ref) => (this[`ref-quarter-${lead.title}`] = ref)}
                  width={this.store.quarterLeadWidth}
                  height={this.store.quarterLeadHeight}
                  onWheel={this.wheel}
                  onMouseDown={this.leadMouseDown}
                  onTouchStart={this.leadTouchStart}
                ></canvas>
                {i === lastSelectedLeadIndex
                  ? this.store.markers.map((marker, i) => {
                      const position = markerPositions[i];
                      return (
                        <React.Fragment key={i}>
                          <Draggable
                            bounds="parent" //{{top: 0, bottom: this.store.quarterLeadHeight - 55}}
                            position={position}
                            onDrag={(e, data) =>
                              this.handleDragCaliper(e, data, i)
                            }
                          >
                            <div
                              {...this.className({
                                marker: true,
                                [this.props.caliperMode]: true,
                              })}
                              style={{
                                touchAction: "none",
                                transform: `translate(21.7699px, 63.2581px)`,
                              }}
                            ></div>
                          </Draggable>
                          <div
                            {...this.className({
                              "marker-line": true,
                              [this.props.caliperMode]: true,
                            })}
                            style={
                              vertical
                                ? {
                                    transform: `translate(0, ${position.y}px)`,
                                    width: this.store.quarterLeadWidth + "px",
                                  }
                                : {
                                    transform: `translate(${position.x}px, 0)`,
                                    height: this.store.quarterLeadHeight + "px",
                                  }
                            }
                            key={`line${i}`}
                          ></div>
                        </React.Fragment>
                      );
                    })
                  : null}
                {this.store.markers.length === 2 &&
                i === lastSelectedLeadIndex ? (
                  <div
                    {...this.className({
                      "caliper-distance": true,
                      [this.props.caliperMode]: true,
                    })}
                    ref={(r) => (this.refCaliperDistance = r)}
                    style={
                      vertical
                        ? {
                            top: _.min(markerPositions.map((m) => m.y)) + "px",
                            bottom:
                              this.store.quarterLeadHeight -
                              _.max(markerPositions.map((m) => m.y)) +
                              "px",
                            left: markerPositions[0].x + "px",
                          }
                        : {
                            top: markerPositions[0].y,
                            left: _.min(markerPositions.map((m) => m.x)) + "px",
                            right:
                              this.store.quarterLeadWidth -
                              _.max(markerPositions.map((m) => m.x)) +
                              "px",
                          }
                    }
                  >
                    <div
                      {...this.className({
                        flip:
                          this.store.quarterLeadHeight - markerPositions[0].y <
                          50,
                      })}
                      onMouseDown={this.startDragBothCalipers}
                      onTouchStart={this.startDragBothCalipers}
                    >
                      {this.caliperDistances}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          <div className="strips">
            {leads.map((lead, i) =>
              !this.store.selectedLead || lead === this.store.selectedLead ? (
                <div className="rhythm-strip" key={i}>
                  {i === 0 ? (
                    <div className="scale-key">0.1mV/.04sec</div>
                  ) : null}
                  <canvas
                    className="rhythm-strip-canvas"
                    ref={(ref) => (this[`ref-strip-${lead.title}`] = ref)}
                    width={this.store.stripWidth}
                    height={this.store.stripHeight}
                  ></canvas>
                  <div className="title">{lead.title}</div>
                </div>
              ) : null,
            )}
            <Draggable
              axis="x"
              onDrag={this.handleDrag}
              defaultPosition={{ x: 0, y: 0 }}
              bounds={{
                left: Math.max(
                  0,
                  this.store.endPosition.x -
                    (5 * this.store.stripWidth) / this.timeRangeSeconds,
                ),
                right: this.store.endPosition.x - this.store.scrubberSizeMin,
              }}
              position={this.store.startPosition}
              onMouseDown={this.scrubberMouseDown}
            >
              <div className="drag-handle start" data-id="start"></div>
            </Draggable>
            <Draggable
              axis="x"
              onDrag={this.handleDrag}
              defaultPosition={{ x: 0, y: 0 }}
              bounds={{
                left: 0,
                right: this.store.stripWidth - this.store.scrubberWidth,
              }}
              position={this.store.middlePosition}
              onMouseDown={this.scrubberMouseDown}
            >
              <div
                className="drag-handle scrubber"
                data-id="middle"
                style={{ width: this.store.scrubberWidth + "px" }}
              ></div>
            </Draggable>
            <Draggable
              axis="x"
              onDrag={this.handleDrag}
              defaultPosition={{
                x: this.props.scrubberWidth || this.store.stripWidth / 4,
                y: 0,
              }}
              bounds={{
                left: this.store.startPosition.x + this.store.scrubberSizeMin,
                right: Math.min(
                  this.store.stripWidth,
                  this.store.startPosition.x +
                    (5 * this.store.stripWidth) / this.timeRangeSeconds,
                ),
              }}
              position={this.store.endPosition}
              onMouseDown={this.scrubberMouseDown}
            >
              <div className="drag-handle end" data-id="end"></div>
            </Draggable>
          </div>
        </div>
      </div>
    );
  }

  componentWillUnmount() {
    this.unlisten();
  }

  componentDidMount() {
    if (this.props.scrubberWidth) {
      this.store.endPosition = {
        x: this.props.scrubberWidth * this.store.stripWidth,
        y: 0,
      };
      this.updateQuarterLeads();
    }
    this.setSizes();
    new ResizeSensor(this.refGrid, this.setSizes);
    if (this.props.selectedLead) {
      this.setLead();
    }
  }

  componentDidUpdate() {
    this.updateQuarterLeads();
    this.store.leads.forEach((lead, i) => {
      this.renderWave(lead, i);
      const quarterLead = this[`ref-quarter-${lead.title}`];
      if (quarterLead) {
        quarterLead.addEventListener("mousewheel", this.preventDefault);
      }
    });
  }

  preventDefault(e) {
    e.preventDefault();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.store.selectedLead) {
      // Vertical center the lead.
      setTimeout(() => {
        this.store.yOffset = 0;
      });
    }
    if (nextProps.selectedLeadIndex !== this.props.selectedLeadIndex) {
      setTimeout(this.setLead);
    }
    // Synchronize zoom between compared ECGs.
    if (
      nextProps.scrubberWidth &&
      nextProps.scrubberWidth.toFixed(3) !==
        (this.store.endRatio - this.store.startRatio).toFixed(3)
    ) {
      this.zoom(
        nextProps.scrubberWidth * this.store.stripWidth -
          this.store.scrubberWidth,
        true,
      );
    }

    if (nextProps.caliperMode !== this.props.caliperMode) {
      this.store.switchCaliperMode(nextProps.caliperMode);
    }
  }

  setLead() {
    this.store.selectedLead = this.props.selectedLead;
    this.setSizes();
  }

  wheel(e) {
    // derive the amount to zoom.
    let z = e.deltaY * 0.2;
    this.zoom(z);
  }

  get timeRangeSeconds() {
    return this.store.timeRangeSeconds;
  }
  get scrubberSizeMax() {
    const timeRangeSeconds = this.timeRangeSeconds;
    const maxTime = 5; // seconds
    return this.store.stripWidth * (maxTime / timeRangeSeconds);
  }

  zoom(scrubberWidthDelta, fromProps) {
    if (!scrubberWidthDelta) {
      return;
    }
    let z = scrubberWidthDelta / 2;

    // Prevent zooming in so far that the scrubber inverts.
    if (
      z < 0 &&
      this.store.startPosition.x - z + this.store.scrubberSizeMin >
        this.store.endPosition.x + z
    ) {
      return;
    }
    // Prevent zooming out too far.
    else if (
      z > 0 &&
      this.store.endPosition.x + z - (this.store.startPosition.x - z) >
        this.scrubberSizeMax
    ) {
      return;
    }

    this.store.startPosition.x -= z;
    this.store.endPosition.x += z;

    // keep handles from zooming off the strip
    if (this.store.startPosition.x < 0) {
      this.store.startPosition.x = 0;
    }
    if (this.store.endPosition.x > this.store.stripWidth) {
      this.store.endPosition.x = this.store.stripWidth;
    }
    this.store.middlePosition.x = this.store.startPosition.x;
    this.store.startPosition = { ...this.store.startPosition };
    this.store.endPosition = { ...this.store.endPosition };
    this.store.middlePosition = { ...this.store.middlePosition };
    this.updateQuarterLeads(this.store.yOffset, fromProps);
  }
  pinchZoom(e) {
    const { distance, startX, endX } = this.pinchZoomInit;
    const originalHandleDistance = endX - startX;
    const newHandleDistance = _.clamp(
      originalHandleDistance / (this.touchDistance(e) / distance),
      this.store.scrubberSizeMin,
      this.scrubberSizeMax,
    );
    this.store.endPosition.x = this.store.startPosition.x + newHandleDistance;
    if (this.store.endPosition.x > this.store.stripWidth) {
      const overshoot = this.store.endPosition.x - this.store.stripWidth;
      this.store.endPosition.x = this.store.stripWidth;
      this.store.startPosition.x -= overshoot;
    }
  }

  leadTouchStart = (e) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      this.store.mouseDownTime = performance.now();
      this.store.mouseDownPosition = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      window.addEventListener("touchmove", this.dragLead);
      window.addEventListener("touchend", this.endDrag);
      this.startLeadDrag(e);
    }
    if (e.touches.length === 2) {
      // start pinch zoom
      this.pinchZoomInit = {
        distance: this.touchDistance(e),
        zoomAmount: this.store.zoomAmount,
        startX: this.store.startPosition.x,
        endX: this.store.endPosition.x,
      };
    }
  };

  touchDistance(e) {
    return Math.sqrt(
      Math.pow(e.touches[0].clientX - e.touches[1].clientX, 2) +
        Math.pow(e.touches[0].clientY - e.touches[1].clientY, 2),
    );
  }
  leadMouseDown(e) {
    this.store.mouseDownPosition = { x: e.clientX, y: e.clientY };
    this.store.mouseDownTime = performance.now();
    e.preventDefault();
    window.addEventListener("mousemove", this.dragLead);
    window.addEventListener("mouseup", this.endDrag);
    this.startLeadDrag(e);
  }
  startLeadDrag(e) {
    this.store.originalY = this.store.yOffset || 0;
    this.store.originalX = this.store.xOffset || 0;
    this.dragTarget = e.currentTarget;
    this.prevMouseEventX = this.store.mouseDownPosition.x;
  }

  dragLead(e) {
    let { movementX, clientX, clientY } = _.get(e, "touches[0]", e);
    if (!movementX) {
      movementX = clientX - this.prevMouseEventX;
      this.prevMouseEventX = clientX;
    }
    let xOffset = movementX / this.store.zoomAmount;

    // scale panning for 12-lead view
    xOffset = (xOffset * this.store.stripWidth) / this.store.quarterLeadWidth;

    // prevent panning off the chart.
    xOffset =
      this.store.startPosition.x - movementX < this.store.scrubberSizeMin
        ? this.store.startPosition.x
        : xOffset;
    xOffset =
      this.store.endPosition.x - movementX > this.store.stripWidth
        ? this.store.endPosition.x - this.store.stripWidth
        : xOffset;

    this.store.startPosition = {
      x: this.store.startPosition.x - xOffset,
      y: 0,
    };
    this.store.endPosition = { x: this.store.endPosition.x - xOffset, y: 0 };
    this.store.middlePosition = { ...this.store.startPosition };

    if (this.store.mouseDownPosition) {
      this.updateQuarterLeads(
        this.store.originalY +
          (clientY - this.store.mouseDownPosition.y) / this.store.zoomAmount,
      );
    }
    if (this.pinchZoomInit && _.get(e, "touches.length") === 2)
      this.pinchZoom(e);
  }

  // handles drag events for panning single leads
  endDrag(e) {
    if (e.touches) {
      if (e.touches.length < 2) this.pinchZoomInit = null;
    }
    if (this.store.mouseDownTime >= performance.now() - 200) {
      this.selectLead(
        _.findIndex(
          this.props.ecg.leads,
          (lead) =>
            lead.title ===
            this.dragTarget.parentElement.getAttribute("data-key"),
        ),
      );
    } else {
      e.preventDefault();
    }
    this.store.mouseDownPosition = null;
    window.removeEventListener("mousemove", this.dragLead);
    window.removeEventListener("mouseup", this.endDrag);
    window.removeEventListener("touchmove", this.dragLead);
    window.removeEventListener("touchend", this.endDrag);
  }

  renderWaveToCanvas(data, canvas, isQuarterLead) {
    if (!canvas) {
      return;
    }

    const zoomFactor = isQuarterLead
      ? this.store.stripWidth / this.store.scrubberWidth
      : 1;
    const context = canvas.getContext("2d");

    // clear the canvas / fill the background.
    context.fillStyle = "#FFFEFE";
    context.save();
    context.beginPath();
    context.rect(0, 0, canvas.width, canvas.height);
    context.fill();

    // Disable zoom on non-grid cells
    const gridWidth = canvas.width * zoomFactor;

    /* Pan */
    let translateY =
      (this.store.selectedLead ? this.store.yOffset : 0) * zoomFactor;
    if (isQuarterLead) {
      context.translate(this.store.xOffset * zoomFactor, translateY);
    } else {
      context.translate(0, 0);
    }

    const samplesPerSecond = this.props.ecg.sampleBase;
    const timeRangeSeconds = data.length / samplesPerSecond;

    var wX = d3
      .scaleLinear()
      .domain([0, timeRangeSeconds])
      .range([0, canvas.width]);
    // scale 0.5mV:0.2sec or 1mV:0.4sec or 2.5mV:1sec

    let domainY =
      (this.store.mVperSecond * timeRangeSeconds * canvas.height) /
      canvas.width;
    var wY = d3
      .scaleLinear()
      .domain([-domainY / 2, domainY / 2])
      .range([canvas.height, 0]);

    /* Grid Lines */
    const lineWidth = {
      thin: 0.3,
      medium: 0.6,
      heavy: 2,
    };
    context.strokeStyle = "#FE6A52";

    // A small box is 1 mm x 1 mm big and represents 0.1 mV x 0.04 seconds.
    // A large box is 5 mm x 5mm big and represents 0.5 mV x 0.2 seconds wide.

    // render 0.2mV second lines
    this.renderVerticalLines({
      wX,
      context,
      timeRangeSeconds,
      wY,
      step: 0.2,
      lineWidth: lineWidth.medium,
      zoomFactor,
      isQuarterLead,
    });
    // render 0.5 mV lines
    this.renderHorizontalLines({
      wY,
      context,
      step: 0.5,
      lineWidth: this.store.selectedLead ? lineWidth.heavy : lineWidth.medium,
      gridWidth,
      zoomFactor,
      isQuarterLead,
    });

    // render 0 line
    context.beginPath();
    context.lineWidth = this.store.selectedLead ? 4 : lineWidth.heavy;
    context.moveTo(0, wY(0));
    context.lineTo(gridWidth * zoomFactor, wY(0));
    context.stroke();

    if (wX(0.04) * zoomFactor > 4) {
      // render 0.1 mV line
      this.renderHorizontalLines({
        wY,
        context,
        step: 0.1,
        lineWidth: this.store.selectedLead ? lineWidth.medium : lineWidth.thin,
        gridWidth,
        zoomFactor,
        isQuarterLead,
      });
      // render 0.04 second lines
      this.renderVerticalLines({
        wX,
        context,
        timeRangeSeconds,
        wY,
        step: 0.04,
        lineWidth: lineWidth.thin,
        zoomFactor,
        isQuarterLead,
      });
    }

    // Render wave.
    context.beginPath();
    context.lineWidth = isQuarterLead
      ? this.store.selectedLead
        ? 2
        : 0.7
      : 0.7 * (0.5 + 0.5 * (canvas.width / 1200));
    var wLine = d3
      .line()
      .x(function (d) {
        return wX(d.x * zoomFactor);
      })
      .y(function (d) {
        return wY(d.y * zoomFactor);
      });
    wLine.context(context)(
      data.map((d, i) => ({ x: i / samplesPerSecond, y: d })),
    );
    context.strokeStyle = "black";
    context.stroke();
    context.restore();
  }

  renderMask(canvas) {
    // render mask
    const context = canvas.getContext("2d");
    context.fillStyle = "rgba(0,0,0,0.1)";
    context.fillRect(0, 0, this.store.startPosition.x, canvas.height);
    context.fillRect(
      this.store.endPosition.x,
      0,
      this.store.stripWidth - this.store.endPosition.x,
      canvas.height,
    );
  }

  renderHorizontalLines({
    wY,
    context,
    step,
    lineWidth,
    gridWidth,
    zoomFactor,
    isQuarterLead,
  }) {
    // don't draw lines that are too close together.
    context.lineWidth = lineWidth;
    context.beginPath();

    let yCoord = step * zoomFactor;
    let yOffset =
      (this.store.selectedLead && isQuarterLead ? this.store.yOffset : 0) *
      zoomFactor;
    while (wY(yCoord) + yOffset > 0) {
      let lineY = wY(yCoord);
      context.moveTo(0, lineY);
      context.lineTo(gridWidth * zoomFactor, lineY);
      yCoord += step * zoomFactor;
    }
    yCoord = -step * zoomFactor;
    while (wY(yCoord) + yOffset < context.canvas.height) {
      let lineY = wY(yCoord);
      context.moveTo(0, lineY);
      context.lineTo(gridWidth * zoomFactor, lineY);
      yCoord -= step * zoomFactor;
    }

    context.stroke();
  }

  renderVerticalLines({
    wX,
    context,
    timeRangeSeconds,
    step,
    lineWidth,
    zoomFactor,
    isQuarterLead,
  }) {
    let lineTime = 0;
    context.lineWidth = lineWidth;
    context.beginPath();
    let yOffset =
      (this.store.selectedLead && isQuarterLead ? this.store.yOffset : 0) *
      zoomFactor;
    while (lineTime <= timeRangeSeconds) {
      let x = wX(lineTime * zoomFactor);
      context.moveTo(x, -yOffset);
      context.lineTo(x, context.canvas.height - yOffset);
      lineTime += step;
    }
    context.stroke();
  }

  renderWave(lead, i) {
    if (lead.values) {
      const quarterChart = this[`ref-quarter-${lead.title}`];
      if (quarterChart) {
        this.renderWaveToCanvas(lead.values, quarterChart, true);
        this.renderMarchOut({ canvas: quarterChart, i });
      }

      const stripChart = this[`ref-strip-${lead.title}`];
      if (stripChart) {
        if (this.stripChartsCache[lead.title] && !this.shouldRenderMarchOut) {
          const stripChartContext = stripChart.getContext("2d");
          stripChartContext.drawImage(this.stripChartsCache[lead.title], 0, 0);
        } else {
          // render the strip once and then cache it for reuse.
          this.renderWaveToCanvas(lead.values, stripChart, false);
          if (this.shouldRenderMarchOut) {
            this.renderMarchOut({ canvas: stripChart, i });
          } else {
            const cacheCanvas = document.createElement("canvas");
            cacheCanvas.height = stripChart.height;
            cacheCanvas.width = stripChart.width;
            cacheCanvas.getContext("2d").drawImage(stripChart, 0, 0);
            this.stripChartsCache[lead.title] = cacheCanvas;
          }
        }
        this.renderMask(stripChart);
      }
    }
  }

  handleDrag(e, data) {
    switch (data?.node.getAttribute("data-id")) {
      case "start":
        this.store.middlePosition = { x: data.x, y: 0 };
        this.store.startPosition = { x: data.x, y: 0 };
        break;
      case "middle":
        this.store.endPosition = { x: data.x + this.store.scrubberWidth, y: 0 };
        this.store.startPosition = { x: data.x, y: 0 };
        this.store.middlePosition = { x: data.x, y: 0 };
        break;
      case "end":
        this.store.endPosition = { x: data.x, y: 0 };
        break;
      default:
        console.error("unexpected data-id for drag handle.");
    }
    this.updateQuarterLeads();
  }

  updateQuarterLeads(yOffset = this.store.yOffset || 0, fromProps) {
    this.store.yOffset = yOffset;
    const zoomFactor = this.store.stripWidth / this.store.scrubberWidth;
    let xOffset =
      (-this.store.startPosition.x * this.store.quarterLeadWidth) /
      this.store.stripWidth;

    // keep zoom centered for 12-lead.
    this.store.zoomAmount = zoomFactor;
    this.store.yOffset = yOffset = this.store.selectedLead ? yOffset : 0;

    // constrain panning.
    xOffset = xOffset > 0 ? 0 : xOffset;
    let maxPan = this.store.quarterLeadWidth * -3 * zoomFactor;
    xOffset = xOffset < maxPan ? maxPan : xOffset;
    this.store.xOffset = xOffset || 0;

    // store the positions of the scrubber as a ratio so that it can be restored after resizing.
    this.store.startRatio = this.store.startPosition.x / this.store.stripWidth;
    this.store.endRatio = this.store.endPosition.x / this.store.stripWidth;
    if (!fromProps) {
      let scrubberWidthRatio = this.store.endRatio - this.store.startRatio;
      scrubberWidthRatio =
        scrubberWidthRatio > 1
          ? 1
          : scrubberWidthRatio < 0
            ? 0
            : scrubberWidthRatio;
      this.props.setScrubberWidth(scrubberWidthRatio);
    }
  }

  setSizes() {
    if (this.refChartRoot) {
      this.refGrid.style.height = this.refChartRoot.clientHeight - 130 + "px";
      this.store.width = this.refChartRoot.clientWidth;
    }
    this.store.stripWidth = this.store.width - 44;
    this.rescaleScrubber();
    this.stripChartsCache = {};

    if (this.refGrid) {
      let gridHeight = this.refGrid.clientHeight;
      this.store.quarterLeadHeight = this.store.selectedLead
        ? gridHeight - 20
        : gridHeight / 3 - 21;
      this.store.quarterLeadWidth = this.store.selectedLead
        ? this.refGrid.clientWidth
        : this.refGrid.clientWidth / 4 - 12;
    }
  }

  rescaleScrubber() {
    this.store.startPosition = {
      x: this.store.startRatio * this.store.stripWidth,
      y: 0,
    };
    this.store.endPosition = {
      x: this.store.endRatio * this.store.stripWidth,
      y: 0,
    };
    this.store.middlePosition = { ...this.store.startPosition };
  }

  scrubberMouseDown() {
    this.store.startPosition = { ...this.store.startPosition };
    this.store.endPosition = { ...this.store.endPosition };
    this.store.middlePosition = { ...this.store.middlePosition };
  }

  handleDragCaliper(e, data, i) {
    const marker = this.store.markers[i];
    // marker.position = {...position};
    marker.position.x = data.x;
    marker.position.y = data.y;
    if (this.props.caliperMode === "horizontal") {
      this.store.markers.forEach((marker) => {
        marker.position.y = data.y;
      });
    } else if (this.props.caliperMode === "vertical") {
      this.store.markers.forEach((marker) => {
        marker.position.x = data.x;
      });
    }
    this.store.storeMarkers();
    this.store.markers = [...this.store.markers];
    if (e && data) {
      this.handleOverDrag(data);
    }
  }

  handleOverDrag(data) {
    // Scrolls the chart when you drag a marker over the side.
    if (data.x > this.store.quarterLeadWidth - 40) {
      this.store.pan(this.store.quarterLeadWidth - 40 - data.x);
    } else if (data.x < 40) {
      this.store.pan(40 - data.x);
    }
  }

  get caliperDistances() {
    if (this.props.caliperMode === "vertical") {
      return (
        Math.abs(
          this.store.markers[0].voltage - this.store.markers[1].voltage,
        ).toFixed(2) + "mV"
      );
    } else {
      let timeDistanceSeconds = Math.abs(
        this.store.markers[0].time - this.store.markers[1].time,
      );
      return `${timeDistanceSeconds.toFixed(2)}s`;
    }
  }

  startDragBothCalipers(e) {
    e.preventDefault();
    if (e.touches) {
      if (e.touches.length > 1) return;
      e = e.touches[0];
    }
    const store = this.store;
    const markers = store.markers;
    const markerOffsets = markers.map((marker) => {
      return {
        x: marker.position.x - e.clientX,
        y: marker.position.y - e.clientY,
      };
    });
    const mouseMove = this.frameDebounce((e) => {
      e.preventDefault();
      if (e.touches) {
        if (e.touches.length > 1) return;
        e = e.touches[0];
      }
      markers.forEach((marker, i) => {
        Object.assign(marker.position, {
          x: e.clientX + markerOffsets[i].x,
          y: e.clientY + markerOffsets[i].y,
        });
      });
      store.storeMarkers();
      const x1 = markers[0].position.x;
      const x2 = markers[1].position.x;
      const quarterLeadWidth = store.quarterLeadWidth;
      if (x2 - x1 < quarterLeadWidth) {
        if (x1 < 40) {
          store.pan(40 - x1);
        } else if (x2 > quarterLeadWidth - 40) {
          store.pan(quarterLeadWidth - 40 - x2);
        }
      }
      this.store.markers = [...this.store.markers];
    });
    const mouseUp = () => {
      window.removeEventListener("mousemove", mouseMove);
      window.removeEventListener("mouseup", mouseUp);
      window.removeEventListener("touchmove", mouseMove);
      window.removeEventListener("touchend", mouseUp);
    };
    window.addEventListener("mousemove", mouseMove);
    window.addEventListener("mouseup", mouseUp);
    window.addEventListener("touchmove", mouseMove);
    window.addEventListener("touchend", mouseUp);
  }

  get shouldRenderMarchOut() {
    return (
      this.props.caliperMode === "horizontal" &&
      (!this.store.selectedLead || this.store.marchOutMode)
    );
  }

  renderMarchOut({ canvas, i }) {
    if (!this.shouldRenderMarchOut) return;

    let caliperPositions = this.store.markers.map((marker) => {
      let isStrip = canvas.classList.contains("rhythm-strip-canvas");
      return this.store.timeToPosition(marker.time, isStrip);
    });
    let distance = Math.abs(caliperPositions[1] - caliperPositions[0]);
    let offset = caliperPositions[0] % distance;
    if (!distance || distance < 2 || isNaN(offset)) return;
    if (this.refCaliperDistance) {
      this.blue = _.get(
        getComputedStyle(this.refCaliperDistance),
        "borderTopColor",
      );
    }
    this.marchOutColor =
      this.refCaliperDistance && i === 0 ? this.blue : "#444";
    let ctx = canvas.getContext("2d");
    ctx.strokeStyle = this.marchOutColor;
    ctx.lineWidth = 1;
    ctx.beginPath();

    let iteration = 0;
    for (let x = offset; x <= canvas.width; x = x + distance) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      if (iteration++ > 1000) break;
    }
    ctx.stroke();
  }

  toggleMarchOut() {
    this.store.marchOutMode = !this.store.marchOutMode;
  }
}

export default withRouter(observer(ECG12Lead));
